import { NavigationHelper } from '@helpers';
import { initialState, questionnaireStore } from '@stores';
import { AppRoutes } from '@types';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

export type DataCollectionShownStep = 'datasetInventory' | 'testing' | 'questionnaire';

interface UseDataCollectionNavigationParams {
  completed: boolean;
  shownStep: DataCollectionShownStep;
  setShownStep: (step: DataCollectionShownStep) => void;
}

export const useDataCollectionNavigation = ({
  completed,
  shownStep,
  setShownStep,
}: UseDataCollectionNavigationParams) => {
  const { events, pathname } = useRouter();
  const [showExitModal, setShowExitModal] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  const explicitelyLeavePage = useRef(false);

  const handleLeavePage = useCallback(
    (e: BeforeUnloadEvent) => {
      NavigationHelper.leavePage(
        e,
        !completed && (shownStep === 'datasetInventory' || shownStep === 'testing' || shownStep === 'questionnaire'),
      );
    },
    [completed, shownStep],
  );

  useEffect(() => {
    const handleRoute = (targetUrl: string) => {
      if (completed) return;
      if (targetUrl.startsWith(`/cal${AppRoutes.dataCollection}`)) {
        questionnaireStore.setQuestionnaireData(initialState);
        setShownStep('datasetInventory');
        return;
      }
      NavigationHelper.handleRouteChange({
        targetUrl,
        completed,
        showTestingView: shownStep === 'testing' || shownStep === 'questionnaire',
        showQuestionnaire: shownStep === 'questionnaire',
        pathname,
        explicitelyLeavePage,
        setShowExitModal,
        setRedirectUrl,
        events,
      });
    };

    events.on('routeChangeStart', handleRoute);
    return () => events.off('routeChangeStart', handleRoute);
  }, [events, completed, pathname, shownStep, setShownStep]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleLeavePage);
    return () => window.removeEventListener('beforeunload', handleLeavePage);
  }, [handleLeavePage]);

  return { showExitModal, setShowExitModal, redirectUrl, explicitelyLeavePage };
};
